import { parseMathSlots } from '../../utils/evaluation/math/mathAnswerSlot';
import { normalizeMathLatex } from '../../utils/evaluation/math/normalizeMathLatex';

// Authoring and display of a Math blank's student slots (PBI 538086): what an empty slot holds on screen,
// the "student slot" key, and how an author's edit is split into the expression and each slot's answer.
// Grading never needs any of it, so it lives beside the field rather than in utils/evaluation.

/**
 * What an EMPTY slot holds while it is on screen: an invisible digit. MathLive sizes an empty slot from the
 * x-height and a filled one from the digit's height, so without it a slot grows — and its fraction or root
 * moves — as soon as a value arrives. `normalizeMathLatex` drops `\phantom`, so it never reaches an
 * answer.
 */
export const EMPTY_MATH_SLOT_FILL = '\\phantom{0}';

/** The id the "student slot" key inserts; replaced by a generated id as soon as the edit is processed. */
export const NEW_MATH_SLOT_ID = 'new';

/** What the author keyboard's "student slot" key inserts — `#@` wraps the current selection. */
export const MATH_SLOT_KEY_INSERT = `\\placeholder[${NEW_MATH_SLOT_ID}]{#@}`;

/**
 * The expression with every slot holding its answer: the solved exercise. `formatSlot`
 * renders one slot; by default it is the MathLive prompt itself, so the author's field keeps its slots.
 */
export function mergeMathSlotAnswers(
  expression: string,
  answers: Record<string, string>,
  formatSlot: (slotId: string, answer: string) => string = (slotId, answer) =>
    `\\placeholder[${slotId}]{${answer || EMPTY_MATH_SLOT_FILL}}`
): string {
  let merged = '';
  let index = 0;
  for (const slot of parseMathSlots(expression)) {
    merged += expression.slice(index, slot.start) + formatSlot(slot.id, answers[slot.id] ?? '');
    index = slot.end;
  }
  return merged + expression.slice(index);
}

/**
 * A slot's body with every slot inside it replaced by what that slot shows, or — when it shows nothing —
 * by its answer. What the author sees is the better source: a stored answer can lag the field (a
 * collaborator's concurrent edit).
 */
function flattenInnerSlots(body: string, answers: Record<string, string>): string {
  return parseMathSlots(body).reduceRight(
    (flat, inner) =>
      flat.slice(0, inner.start) + (normalizeMathLatex(inner.body) || answers[inner.id] || '') + flat.slice(inner.end),
    body
  );
}

/**
 * Apply one author edit of the expression: whatever the author put INSIDE a slot leaves the expression and
 * becomes that slot's answer (shown in the slot's own field), so the stored expression keeps empty slots.
 *
 *  - a slot the key just inserted (id `NEW_MATH_SLOT_ID`) gets a generated id; the selection it wrapped
 *    becomes its answer, any slots in that selection flattened to their values;
 *  - the key pressed inside an existing slot converts that slot back to plain content — what it shows
 *    (or, when it shows nothing, its answer) is written into the expression in its place;
 *  - a slot that repeats an earlier slot's id (a copy-paste) becomes a new slot with the same answer.
 *
 * `answers` maps slot id → answer and is not mutated; the result carries the updated map.
 */
export function applyMathSlotExpressionEdit(
  latex: string,
  answers: Record<string, string>,
  createId: () => string
): { expression: string; answers: Record<string, string> } {
  const nextAnswers = { ...answers };
  const seenIds = new Set<string>();
  let expression = '';
  let index = 0;

  for (const slot of parseMathSlots(latex)) {
    expression += latex.slice(index, slot.start);
    index = slot.end;

    const innerSlots = parseMathSlots(slot.body);
    const content = normalizeMathLatex(flattenInnerSlots(slot.body, nextAnswers));
    // Slots swallowed by this one (a selection across slots) are gone, and their answers with them.
    for (const inner of innerSlots) delete nextAnswers[inner.id];

    const convertsBack = innerSlots.some(inner => inner.id === NEW_MATH_SLOT_ID);
    if (convertsBack && slot.id !== NEW_MATH_SLOT_ID) {
      expression += content || nextAnswers[slot.id] || '';
      delete nextAnswers[slot.id];
      continue;
    }

    const id = slot.id === NEW_MATH_SLOT_ID || seenIds.has(slot.id) ? createId() : slot.id;
    if (id !== slot.id && nextAnswers[slot.id] !== undefined && slot.id !== NEW_MATH_SLOT_ID) {
      nextAnswers[id] = nextAnswers[slot.id];
    }
    seenIds.add(id);

    // A slot on screen is empty (its answer lives in its own field), so only what the author just put
    // in it is news: a selection the key wrapped, or something typed into it.
    if (content) nextAnswers[id] = content;
    expression += `\\placeholder[${id}]{}`;
  }
  expression += latex.slice(index);

  return { expression, answers: nextAnswers };
}
